import React, { useEffect } from 'react';
import { SignIn, useUser } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import TroubleSigningIn from '../components/TroubleSigningIn';

const SignInPage: React.FC = () => {
  const { isLoaded, isSignedIn } = useUser();

  // Where to go after signing in. A page that requires auth (e.g. the Funeral
  // Guide) passes ?redirect_url=/its/path so the visitor lands back on what
  // they actually came for, instead of being dumped on the dashboard
  // (real-user report, 2026-07-17). Security: same-origin relative paths only.
  const params = new URLSearchParams(window.location.search);
  const rawRedirect = params.get('redirect_url') || '';
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/dashboard';

  useEffect(() => {
    document.title = 'Cyrano - Eulogy Generator';
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  // Already signed in → straight to where they were headed
  if (isLoaded && isSignedIn) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-background px-4 py-8">
      <div className="w-full max-w-[480px]">
        {/* Logo Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img 
              src="/Assets/GhostQuillLogo.png" 
              alt="Cyrano" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-serif text-[2.5rem] font-semibold text-space-cadet">
              Cyrano
            </span>
          </div>
          <p className="text-cool-gray text-base italic">Eulogy Assistant</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-5 sm:p-8 md:p-10 shadow-[0_8px_32px_rgba(25,40,92,0.12)] border border-space-cadet/8">
          {/* Welcome Text */}
          <div className="mb-8 text-left">
            <h1 className="font-serif text-[1.5rem] sm:text-[2rem] font-semibold text-space-cadet mb-2">
              Welcome Back
            </h1>
            <p className="text-cool-gray text-base text-left">
              Sign in to continue creating meaningful tributes
            </p>
          </div>

          {/* Clerk SignIn Component */}
          <SignIn
            appearance={{
              elements: {
                rootBox: 'w-full',
                card: 'shadow-none border-0 p-0',
                headerTitle: 'hidden',
                headerSubtitle: 'hidden',
                socialButtonsBlockButton: 'w-full py-3.5 px-4 border-2 border-cool-gray/20 rounded-lg bg-white text-elegant-black hover:border-space-cadet hover:bg-space-cadet/[0.02] transition-all flex items-center justify-center gap-3 text-[0.95rem] font-medium',
                socialButtonsBlockButtonText: 'text-elegant-black',
                dividerLine: 'bg-cool-gray/20',
                dividerText: 'text-cool-gray text-sm',
                formFieldLabel: 'text-space-cadet font-medium text-[0.95rem] mb-2',
                formFieldInput: 'w-full py-3.5 px-4 border-2 border-cool-gray/20 rounded-lg text-base text-elegant-black focus:border-space-cadet focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]',
                formButtonPrimary: 'w-full py-4 bg-space-cadet hover:bg-space-cadet/90 text-white rounded-lg font-medium text-base transition-all hover:translate-y-[-1px] hover:shadow-[0_4px_12px_rgba(25,40,92,0.2)]',
                footerActionLink: 'text-space-cadet hover:text-space-cadet/80 font-medium',
                formFieldInputShowPasswordButton: 'text-space-cadet hover:text-space-cadet-2',
                identityPreviewText: 'text-space-cadet',
                identityPreviewEditButton: 'text-space-cadet hover:text-space-cadet/80',
              },
              variables: {
                colorPrimary: '#19285c',
                colorText: '#060001',
                colorTextSecondary: '#8c83b4',
                borderRadius: '0.5rem',
              }
            }}
            signUpUrl={rawRedirect ? `/sign-up?redirect_url=${encodeURIComponent(redirectTo)}` : '/sign-up'}
            afterSignInUrl={redirectTo}
            redirectUrl={redirectTo}
          />

          <TroubleSigningIn />

          {/* Back Link */}
          <div className="text-center mt-3">
            <a
              href="/landing.html"
              className="text-cool-gray text-sm inline-flex items-center gap-2 hover:text-space-cadet transition-colors"
            >
              ← Back to home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
